import { useEffect, useState } from "react";
import useGetEmployeeAdOverrideGroup from "../hooks/useGetEmployeeAdOverrideGroup";
import { toast } from "components/toastNotification";
import EditButton from "components/EditButton";
import { addAdOverrideGroup, editOverrideGroup } from "../atoms/atom";
import { useRecoilState, useRecoilValue } from "recoil";
import MaterialTableTS from "components/tables/MaterialTableTS";
import AddButton from "components/AddButton";
import { PERMISSIONS } from "modules/PermissionsConstants";
import { permissionsSelector } from "modules/Login/selectors/permissionSelector";
import TableFilters from "../../TableFilters";
import { notificationFilterById } from "actions/Utilities/NotificationFilter";
import { useLocation } from "react-router-dom";
import EmployeeAdOverrideGroup from "../forms/EmployeeAdOverrideForm";
import { useUpdateEmployeeAdOverrideGroup } from "../hooks/useUpdateEmployeeAdOverrideGroup";
import { IEmployeeAdOverrideGroup } from "../types";
import { useMutation, useQueryClient } from "react-query";
import TextInput from "components/TextInput";
import CancelButton from "components/CancelButton";
import SaveButton from "components/SaveButton";
import ConfirmBoxModal from "components/ConfirmBoxModal";
import { deleteEmployeeAdOverrideGroup } from "../api";
import ConfirmBoxDelete from "components/ConfirmBoxDelete";

export const EmployeeAdOverrideGroupTable = () => {
  //#region Data
  const {
    data: employeeAdOverrideData,
    isLoading: isFetching,
    isSuccess: eemployeeAdOverrideSuccess,
  } = useGetEmployeeAdOverrideGroup();

  //#region Update
  const {
    mutate: updateAdOverrideGroup,
    isSuccess,
    isError,
    data,
    reset,
  } = useUpdateEmployeeAdOverrideGroup();

  const [employeeAddOverrideList, setEmployeeAddOverrideList] = useState([]);
  const [employeeAddOverrideFilteredData, setEmployeeAddOverrideFilteredData] =
    useState([]);
  const [actionAddState, setActionAddState] =
    useRecoilState(addAdOverrideGroup);
  const [actionEditState, setActionEditState] =
    useRecoilState(editOverrideGroup);
  const [rowForEdit, setRowForEdit] = useState<IEmployeeAdOverrideGroup>();
  const [editRowId, setEditRowId] = useState([]);
  const [openModalEdit, setOpenModalEdit] = useState(false);
  const [dataChange, setDataChange] = useState(false);
  const [hasValue, setHasValue] = useState(true);
  const [checkStatus, setCheckStatus] = useState(false);
  const [hideColumn, setHideColumn] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [disabledButton, setDisabledButton] = useState(false);
  const [newData, setNewData] = useState<IEmployeeAdOverrideGroup>();
  const [filterText, setFilterText] = useState("");
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [selectedRows, setSelectedRows] = useState<any>([]);

  //#region Permission
  const userPermissions = useRecoilValue(permissionsSelector);
  const editPermission =
    PERMISSIONS.permission_utilities_employeeadoverridegroup_edit;
  const addPermission =
    PERMISSIONS.permission_utilities_employeeadoverridegroup_add;
  const deletePermission =
    PERMISSIONS.permission_utilities_employeeadoverridegroup_delete;
  //#endregion

  let location = useLocation();
  let recordIdFromRecent;
  const queryClient = useQueryClient();

  if (location.state == null) {
    //Do nothing
  } else {
    recordIdFromRecent = location.state;
  }

  //#region UseEffect for Data
  useEffect(() => {
    if (eemployeeAdOverrideSuccess && employeeAdOverrideData) {
      if (employeeAdOverrideData?.status === 200) {
        setEmployeeAddOverrideList(
          employeeAdOverrideData?.data.map((data) => ({ ...data }))
        );
      } else {
        toast.fire(
          "Server Error",
          "Cannot retrieve Employee AD Override Group Data",
          "error"
        );
      }
      setEmployeeAddOverrideList([]);
    }
  }, [employeeAdOverrideData, eemployeeAdOverrideSuccess]);
  //#endregion

  //#region Filter and Redirection
  const [adOverrideGroupId, setAdOverrideGroupId] = useState("");
  useEffect(() => {
    if (adOverrideGroupId?.includes(".")) {
      let adoverrideIds = adOverrideGroupId.split(".");
      const adoverrideArr = new Set(adoverrideIds.map(Number));
      const filteredData = employeeAdOverrideData?.data?.filter((x) =>
        adoverrideArr.has(x.adoverrideGroupId)
      );
      setEmployeeAddOverrideFilteredData(filteredData);
    } else {
      const filteredData = employeeAdOverrideData?.data?.filter(
        (x) => x.adoverrideGroupId === Number(adOverrideGroupId)
      );
      setEmployeeAddOverrideFilteredData(filteredData);
    }
  }, [adOverrideGroupId, employeeAdOverrideData?.data]);

  useEffect(() => {
    if (!recordIdFromRecent) {
      localStorage.removeItem("NotifFilter");
      localStorage.removeItem("ModuleFilter");
      return;
    }

    const fetchNotification = async () => {
      const result = await notificationFilterById(
        recordIdFromRecent.recordId,
        recordIdFromRecent.moduleName,
        recordIdFromRecent.subModuleName
      );

      setEmployeeAddOverrideFilteredData(result);
      setAdOverrideGroupId(recordIdFromRecent.recordId);
    };

    fetchNotification();
  }, [recordIdFromRecent]);

  useEffect(() => {
    if (filterText) {
      setEmployeeAddOverrideList(
        employeeAdOverrideData?.data.filter((x) =>
          x.adoverrideGroupName
            .toLowerCase()
            .includes(filterText.trim().toLowerCase())
        )
      );
    } else {
      setEmployeeAddOverrideList(employeeAdOverrideData?.data);
    }
  }, [filterText, employeeAdOverrideData?.data]);
  //#endregion

  //#region Reset UseEffect
  useEffect(() => {
    setDataChange(false);
    setActionAddState(false);
    setActionEditState(false);
    setEditRowId([]);
  }, []);
  //#endregion

  //#region Update Functions
  const handleEdit = (rowDetails) => {
    setRowForEdit({ ...rowDetails, isActive: rowDetails.isActive });
    setActionEditState(true);
    setHideColumn(true);
    setDataChange(false);
    setEditRowId(rowDetails.adoverrideGroupId);
    setCheckStatus(rowDetails.isActive);
  };

  const handleEditFieldChange = (source, fieldname, inputvalue) => {
    setDataChange(false);
    inputvalue = inputvalue.trim();

    if (source === "AdOverrideGroup" && fieldname === "adoverrideGroupName") {
      setDataChange(true);

      newData!.adoverrideGroupName = inputvalue;

      inputvalue === "" ? setHasValue(false) : setHasValue(true);
    }
  };

  const handleCheckBox = (check) => {
    const checked = check.target.checked;

    setDataChange(true);
    checked ? setCheckStatus(true) : setCheckStatus(false);
    newData!.isActive = checked;
  };

  const handleSave = (data) => {
    if (
      rowForEdit?.adoverrideGroupName === data?.adoverrideGroupName &&
      rowForEdit?.isActive === data?.isActive
    ) {
      setIsSaving(false);
      toast.fire("Failed", "No changes has been made.", "warning");
      queryClient.invalidateQueries("GetAdOverrideGroup");
      setOpenModalEdit(false);
    } else {
      updateAdOverrideGroup(data);
      setEditRowId([]);
      setDataChange(false);
      setOpenModalEdit(false);
      setActionEditState(false);
      setHideColumn(false);
      setDisabledButton(false);
    }
  };
  //#endregion

  //#region Handle Cancel
  const handleCancel = () => {
    setActionEditState(false);
    setRowForEdit(undefined);
    setEditRowId([]);
    setDataChange(false);
    setHideColumn(false);
    setDisabledButton(false);
    queryClient.invalidateQueries("GetAdOverrideGroup");
  };
  //#endregion

  //#region Table Columns
  const alignCenter = "center" as
    | "center"
    | "inherit"
    | "justify"
    | "left"
    | "right"
    | undefined;

  const customSortHandler = (a, b) => {
    if (!a && !b) return 0;
    if (!a) return 1;
    if (!b) return -1;

    return a.localeCompare(b);
  };

  const matColumns = [
    {
      title: "AD Override Group Name",
      field: "adoverrideGroupName",
      customSort: (a, b) => {
        const nameA = a.adoverrideGroupName?.trim();
        const nameB = b.adoverrideGroupName?.trim();

        return customSortHandler(nameA, nameB);
      },
      render: (rowData) => {
        return rowData.adoverrideGroupId === editRowId ? (
          <div className="flex items-center space-x-1 cursor-pointer">
            <TextInput
              placeholder="AD Override Group"
              defaultValue={rowData.adoverrideGroupName}
              onChange={(inputdata) => {
                handleEditFieldChange(
                  "AdOverrideGroup",
                  "adoverrideGroupName",
                  inputdata.target.value
                );
              }}
              value={undefined}
            />
          </div>
        ) : (
          <span>{rowData.adoverrideGroupName}</span>
        );
      },
    },
    {
      title: "Created By",
      field: "createdBy",
      hidden: hideColumn,
      customSort: (a, b) => {
        const aDate = a.createdBy?.trim();
        const bDate = b.createdBy?.trim();

        return customSortHandler(aDate, bDate);
      },
      render: (rowData) => {
        return rowData.adoverrideGroupId === editRowId ? (
          <div className="flex items-center space-x-1 cursor-pointer"></div>
        ) : (
          <>{rowData.createdBy}</>
        );
      },
    },
    {
      title: "Created Date",
      field: "createdDate",
      hidden: hideColumn,
      render: (rowData) => {
        return rowData.adoverrideGroupId === editRowId ? (
          <div className="flex items-center space-x-1 cursor-pointer"></div>
        ) : (
          <>
            {rowData?.createdDate
              ? new Date(rowData.createdDate).toLocaleDateString()
              : ""}
          </>
        );
      },
    },
    {
      title: "Modified By",
      field: "modifiedBy",
      hidden: hideColumn,
      customSort: (a, b) => {
        const aDate = a.modifiedBy?.trim();
        const bDate = b.modifiedBy?.trim();

        return customSortHandler(aDate, bDate);
      },
      render: (rowData) => {
        return rowData.adoverrideGroupId === editRowId ? (
          <div className="flex items-center space-x-1 cursor-pointer"></div>
        ) : (
          <>{rowData.modifiedBy}</>
        );
      },
    },
    {
      title: "Modified Date",
      field: "modifiedDate",
      hidden: hideColumn,
      render: (rowData) => {
        return rowData.adoverrideGroupId === editRowId ? (
          <div className="flex items-center space-x-1 cursor-pointer"></div>
        ) : (
          <>
            {rowData?.modifiedDate
              ? new Date(rowData.modifiedDate).toLocaleDateString()
              : ""}
          </>
        );
      },
    },
    {
      title: "Status",
      field: "isActive",
      render: (rowData) => {
        return rowData.adoverrideGroupId === editRowId ? (
          <div className="flex space-x-1 cursor-pointer">
            <input
              type="checkbox"
              className="flex items-center space-x-1 cursor-pointer mt-0.5"
              defaultChecked={rowData.isActive}
              onClick={(e) => {
                handleCheckBox(e);
              }}
            />
            <label className="items-center cursor-pointer text-green-600 text-xs">
              {checkStatus ? "Uncheck to Deactivate" : "Check to Activate"}
            </label>
          </div>
        ) : (
          <div>
            <div className="font-sans text-left mr-4 text-xs">
              {rowData.isActive ? "Active" : "Inactive"}
            </div>
          </div>
        );
      },
    },
    {
      title: "Action/s",
      align: alignCenter,
      render: (rowData) => {
        // Check if user has edit permission
        if (!userPermissions.includes(editPermission)) return null;

        const isEditing = rowData.adoverrideGroupId === editRowId;
        const saveDisabled = !hasValue || !dataChange;

        // render save button for edited data
        if (isEditing) {
          return (
            <div className="flex items-center justify-center space-x-2 w-full mr-2">
              <SaveButton
                className={`
                  flex items-center shadow-md text-xs font-semibold text-white gap-x-1 
                  rounded-full bg-senary hover:bg-senary px-3 py-1 
                  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-senary focus:ring-opacity-70
                  ${saveDisabled ? "opacity-50" : "opacity-100"}
                `}
                disabled={saveDisabled}
                onClick={() => setOpenModalEdit(true)}
              />
              <CancelButton
                onClick={(event) => {
                  event.preventDefault();
                  handleCancel();
                }}
              />
            </div>
          );
        }

        // Render Edit button for non-editing rows
        return (
          <div className="flex items-center justify-center mr-5">
            <EditButton
              className={`
                rounded-full items-center bg-quaternary text-white hover:bg-hquaternary
                ${disabledButton ? "opacity-50" : "opacity-100"}
              `}
              disabled={disabledButton}
              onClick={() => {
                setDisabledButton(true);
                setActionEditState(true);
                handleEdit(rowData);
                setNewData(rowData);
              }}
            />
          </div>
        );
      },
    },
  ];
  //#endregion

  //#region Update Result
  if (isSuccess && data) {
    if (data.data === "Duplicate") {
      toast.fire(
        "Failed",
        "Employee AD Override Group already exists.",
        "error"
      );
      setIsSaving(!isSaving);
      queryClient.invalidateQueries("GetAdOverrideGroup");
    } else if (data.status === 201 || data.status === 200) {
      toast.fire(
        "Success",
        "Employee AD Override Group updated successfully.",
        "success"
      );
      queryClient.invalidateQueries("GetAdOverrideGroup");
      handleCancel();
      setRowForEdit(undefined);
      setActionEditState(false);
    } else if (data.status === 500) {
      if (
        data.data.message ===
        "Service not available, closing transmission channel. The server response was: 4.3.2 Service not available"
      ) {
        toast.fire(
          "Warning",
          "New Employee AD Override Group updated successfully, but " +
            data.data.message,
          "warning"
        );
        setIsSaving(false);
        handleCancel();
        setRowForEdit(undefined);
      } else {
        setIsSaving(false);
        setRowForEdit(undefined);
        toast.fire(
          "Failed",
          "Failed to Update Employee AD Override Group! No Data Saved",
          "error"
        );
      }
      queryClient.invalidateQueries("GetAdOverrideGroup");
      setActionEditState(false);
    }
    reset();
  }
  if (isError) {
    setIsSaving(false);

    toast.fire(
      "Server Error",
      "Failed to Update Employee AD Override Group! No Data Saved",
      "error"
    );
    queryClient.invalidateQueries("GetAdOverrideGroup");
    setActionEditState(false);
  }
  //#endregion

  //#region Deactivate
  const deactivateAdOverride = useMutation(
    (ids: number[] | undefined) => deleteEmployeeAdOverrideGroup(ids),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["GetAdOverrideGroup"]);
        toast.fire(
          "Success",
          "Employee AD Override Group were deactivated successfully",
          "success"
        );
      },
      onError: () => {
        toast.fire(
          "Bulk deactivation of Employee AD Override Group failed",
          "Server Error: Cannot deactivate Employee AD Override Group",
          "error"
        );
      },
    }
  );

  const handleSelectedRowsChange = (rows) => {
    const forDelete = rows
      .filter((x) => x.isActive)
      .map((r) => {
        const { tableData, ...rows } = r;
        return rows;
      });
    setSelectedRows(forDelete);
  };

  const deleteMessage = (
    <div className="text-gray-500 font-normal">{`Are you sure you want to deactivate ${
      selectedRows?.length
    } ${
      selectedRows?.length > 1
        ? "Employee AD Override Groups"
        : "Employee AD Override Group"
    }?`}</div>
  );

  const handleDelete = () => {
    const ids = selectedRows.map((row) => row.adoverrideGroupId);
    setSelectedRows([]);
    setOpenModalDelete(false);
    deactivateAdOverride.mutate(ids);
  };
  //#endregion

  return (
    <div className="animate-fade-in-down p-4 bg-white">
      {!actionAddState && (
        <>
          <div className="flex justify-between text-xs items-center w-full">
            <TableFilters
              moduleDetails={"adoverrideGroup"}
              textInputName={"adoverrideGroupName"}
              columnDetails={"AD Override Group Name"}
              filterText={setFilterText}
              disabledButton={actionEditState}
            />

            {userPermissions.includes(addPermission) && (
              <AddButton
                buttonText="New AD Override Group"
                onClick={() => setActionAddState(!actionAddState)}
                disabled={actionEditState}
              />
            )}
          </div>
          <div>
            {userPermissions.includes(deletePermission) ? (
              <div className="text-secondary italic text-xs3 my-2">
                <p className="font-bold italic inline-block mr-1">Note:</p>
                <span>Tick the box to deactivate some data.</span>
              </div>
            ) : (
              <div className="mb-4"></div>
            )}
          </div>
          <div className="">
            <MaterialTableTS
              data={
                employeeAddOverrideFilteredData?.length > 0
                  ? employeeAddOverrideFilteredData
                  : employeeAddOverrideList
              }
              showTitle={true}
              columns={matColumns}
              isLoading={isFetching}
              onSelectionChange={handleSelectedRowsChange}
              selectedRows={selectedRows}
              selection={
                !actionAddState &&
                !actionEditState &&
                userPermissions.includes(deletePermission)
              }
              setConfirmModalOpen={setOpenModalDelete}
            />
          </div>
          <ConfirmBoxModal
            ModalTitle="Update Employee AD Override Group"
            qConfirmation=" Are you sure you want to update this?"
            isOpen={openModalEdit}
            onClose={() => {
              setOpenModalEdit(false);
              setIsSaving(false);
            }}
            onClickFunction={() => {
              handleSave(newData);
              setIsSaving(false);
            }}
            cancelButtonRef={undefined}
          />
          <ConfirmBoxDelete
            ModalTitle="Deactivate Employee AD Override Group"
            qConfirmation={deleteMessage}
            isOpen={openModalDelete}
            onClose={() => {
              setOpenModalDelete(false);
            }}
            onClickFunction={handleDelete}
            cancelButtonRef={undefined}
          />
        </>
      )}
      {actionAddState && (
        <EmployeeAdOverrideGroup
          setIsAddState={setActionAddState}
          adOverrideData={
            employeeAddOverrideFilteredData?.length > 0
              ? employeeAddOverrideFilteredData
              : employeeAddOverrideList
          }
        />
      )}
    </div>
  );
};
